import _WidgetBaseSP from 'more/_WidgetBaseSP';
import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './EntityExistCheck.html?raw';

import lang from 'dojo/_base/lang';
import Tooltip from '../../moreCore/UI/Floating/Tooltip';
import StaticTable from '../../moreCore/UI/StaticTable/StaticTable';
import GenericDialog from '../../moreCore/UI/GenericDialog/GenericDialog';


interface EntityExistCheckConfig {
    /** Wenn 'background' - läuft im Hintergrund, wird nur angezeigt, wenn ein Eintrag in der Datenbank gefunden wird */
    operationMode: "background" | "open",
    /** Wenn 'true' -  gibt isValid() 'false' zurück wenn die Request noch ausgeführt wird. Default: false */
    requestValidation?: boolean,
    /** Wenn 'false' -  gibt isValid() 'false' zurück wenn vorhandene Einträge gefunden werden. Default: false */
    validWhenFound?: boolean,
    /** Aktiviert RowClick in der Ergebnisliste. Default: false */
    entityListRowClickEnabled?: boolean,
    /** Funktion für Rowclick */
    entityListTableRowClickCallback?: StaticTable.rowclickCallback
    listDlgHeader?: string,
    listDlgSubtitle?: string
}

class EntityExistCheck extends compose(_WidgetBaseSP) {
    templateString: any = template;

    private operationMode: "background" | "open" = "background";
    private requestValidation: boolean = false;
    private validWhenFound: boolean = false;
    private isFuzzySearch: boolean = false;
    private requestReady: boolean = true;
    private responseIsEmpty: boolean;

    private entityListRowClickEnabled: boolean = false;
    private entityListTableRowClickCallback: StaticTable.rowclickCallback = null;

    private listDlgHeader: string = (this as any).translate("EntityExistCheck_FounEntriesList_Header");
    private listDlgSubtitle: string = null;

    private mainContainer: HTMLElement;
    private messageContainer: HTMLElement;
    private btnShowList: HTMLButtonElement;

    private warningTooltip: MdsLax<InstanceType<typeof Tooltip>> = null;

    private foundList: Array<any> = [];
    private tableLayout: StaticTable.Layout = null;
    private _delayHandle;


    private _onClickShowList() {

        if (this.foundList.length === 0 && !this.tableLayout) return;

        let table = new StaticTable();
        table.setLayout(this.tableLayout);
        table.setData(this.foundList);
        table.setCyAttribute("entityExistsCheck");

        let dlg = new GenericDialog();

        dlg.show(table,
            this.listDlgHeader,
            this.listDlgSubtitle
        );

        if (this.entityListRowClickEnabled) {
            table.setRowClick(this.entityListTableRowClickCallback);
            table.on("rowClicked", () => { dlg.close() });
        }
    }

    /**
     * StaticTable Callback
     * @param callback Aufgerufen wird das Callback mit diesen Parametern: StaticTable.StaticTableRow, StaticTable.StaticTableRowGroup, lineIndex
     */
    private enableEntitylistRowClickCallback(callback: StaticTable.rowclickCallback, enable: boolean = false) {

        if (callback != null && typeof (callback) == "function") {

            this.entityListTableRowClickCallback = callback;
            this.entityListRowClickEnabled = enable;

            return;
        }
        this.entityListRowClickEnabled = false;
    }

    /**
     * Überprüft, ob bereits Entitäten einer bestimmten Klasse/Typs mit übergebenen Attributen in der Datenbank vorhanden sind.
     * @param type Typ - muss es in der API konfiguriert werden!
     * @param attributes Attributes Object: { ... name : value }
     */
    requestCheck(type: string, attributes: Record<string, any>) {
        this.hourGlassStart();
        this.setView("working");
        this.requestReady = false;
        if (this._delayHandle) clearTimeout(this._delayHandle);

        setTimeout(lang.hitch(this, this.sendCheckRequest, type, attributes), 300);
    }

    private sendCheckRequest(type: string, attributes: Record<string, any>) {

        if (Object.keys((attributes ?? {})).length == 0){
            this.setView("notFound");
            this.StandbyStop();
            this.hourGlassEnd();
            this.requestReady = true;
            this._emitRequestDone();
            return;
        }
        let filteredAttributes:Record<string, any> = Object.entries(attributes)
            .filter(([key, value]) => {
                let keepEntry = true;
                if (typeof value == 'undefined'){
                    keepEntry = false;
                }else{
                    if (typeof value == 'string'){
                        if (value.trim() == ""){
                            keepEntry = false;
                        }
                    }
                }
                return keepEntry;
            });
        if ((filteredAttributes?.length ?? 0) == 0) {
            this.setView("notFound");
            this.StandbyStop();
            this.hourGlassEnd();
            this.requestReady = true;
            this._emitRequestDone();
            return;
        }

        this.RequestManager.callfunction(
            "Webtool_API_EntityExistCheck_RequestCheckForExistingEntity",
            {
                type: type,
                attributes: attributes,
                useFuzzySearch: true //fuzzy search soll verwendet werden (parameter fuer abwertscomp., alte web clienten haben param nicht)
            },
            lang.hitch(this, this._onCheckResponse),
            (err) => {
                this.showToast(err.Message);
                this.StandbyStop();
                this.requestReady = true;
                this._emitRequestDone();
            }
        )
    }

    private _onCheckResponse(response: { foundList: Array<any>, tableLayout: { layouts: Array<StaticTable.Layout> }, absoluteSearchResult: boolean }) {
        this.hourGlassEnd();

        if (typeof response["absoluteSearchResult"] != 'boolean') {
            response["absoluteSearchResult"] = true; //non-fuzzy, default
        }

        this.isFuzzySearch = !response.absoluteSearchResult; //wenn absolute, dann nicht fuzzy

        this.foundList = response.foundList;
        this.tableLayout = response.tableLayout.layouts[0];

        this.responseIsEmpty = response.foundList.length === 0;

        if (this.operationMode === "background") this.UiModder.domClass.toggle(this.mainContainer, "w3-hide", this.responseIsEmpty);

        if (this.warningTooltip) this.warningTooltip.destroy();

        if (!this.responseIsEmpty) {
            if (response.absoluteSearchResult == true) {
                this.setView("found");
                this.createTooltipMessage(response.foundList.length);
            } else {
                this.setView("found_fuzzy");
                this.createTooltipMessage(response.foundList.length, true);
            }

        } else {
            this.setView("notFound");
        }
        this.requestReady = true;
        this._emitRequestDone();
    }
    private _emitRequestDone() {
        this.emit("request_done");
    }
    private createTooltipMessage(count: number, isFuzzySearch: boolean = false): void {

        let msg: HTMLElement = this.UiModder.domConstruct.create("div");

        this.UiModder.domConstruct.create("h5", { innerHTML: `${this.translate("EntityExistCheck_Warning_Header")}: ${count}` }, msg);
        this.UiModder.domConstruct.create("p", { innerHTML: this.translate("EntityExistCheck_Warning_Message") }, msg);
        if (!this.validWhenFound && isFuzzySearch == false) {
            this.UiModder.domConstruct.create(
                "h6",
                {
                    innerHTML: this.translate("EntityExistCheck_Label_CannotSave"),
                    class: "warningTXT"
                },
                msg
            );
        }


        this.warningTooltip = new Tooltip();
        this.warningTooltip.setup({
            content: msg,
            targetnode: this.mainContainer,
            enableHover: true,
            followMouse: true,
            icon: "fas fa-warning",
            delayMS: 200
        });
    }

    private setOperationMode(operationMode: "background" | "open") {
        this.operationMode = operationMode;

        this.UiModder.domClass.toggle(this.mainContainer, "w3-hide", operationMode === "background");
    }
    private setView(type: "start" | "working" | "found" | "notFound" | 'found_fuzzy') {
        let view: { text: string, backgroundClass: string };

        switch (type) {
            case "start":
                view = {
                    text: this.translate("EntityExistCheck_Label_Start"),
                    backgroundClass: "infoBG"
                };
                this.hideNode(this.btnShowList)
                break;
            case "working":
                view = {
                    text: this.translate("EntityExistCheck_Label_Working"),
                    backgroundClass: "w3-disabled"
                }
                this.hideNode(this.btnShowList)
                break;
            case "found":
                view = {
                    text: this.translate("EntityExistCheck_Label_Warning"),
                    backgroundClass: "warningYellowBG w3-pale-red"
                }
                this.hideNode(this.btnShowList, false);
                break;
            case 'found_fuzzy':
                view = {
                    text: this.translate("EntityExistCheck_Label_Warning_Fuzzy"),
                    backgroundClass: "warningYellowBG"
                }
                this.hideNode(this.btnShowList, false);
                //es gibt keinen save-block, deshalb muessen wir fuer fuzzy search nur den anzeige-text anpassen
                break;
            case "notFound":
                view = {
                    text: this.translate("EntityExistCheck_Label_NotFound"),
                    backgroundClass: "successBG"
                }
                this.hideNode(this.btnShowList)
                break;
        }

        this.messageContainer.textContent = view.text;
        this.UiModder.domClass.replace(this.mainContainer, view.backgroundClass, "infoBG successBG w3-disabled w3-pale-red warningYellowBG")
    }

    isValid(): boolean {
        if (!this.requestValidation) return true;
        if (this.requestReady && this.isFuzzySearch){
            return true; //fuzzy search blockt save nicht
        }
        if (this.requestReady && this.validWhenFound) return true;
        if (this.requestReady && !this.validWhenFound) return this.responseIsEmpty;

        return false;
    }

    setup(config: EntityExistCheckConfig): void {

        if (!config) {
            this.showToast("ERROR beim EntityExistCheck - no config!", toasterIcons.error);
            return;
        }

        if (typeof config.requestValidation != "undefined") this.requestValidation = config.requestValidation;
        if (typeof config.validWhenFound != "undefined") this.validWhenFound = config.validWhenFound;

        this.setOperationMode(config.operationMode);
        this.enableEntitylistRowClickCallback(config.entityListTableRowClickCallback, config.entityListRowClickEnabled)

        if (config.listDlgHeader) this.listDlgHeader = config.listDlgHeader;
        if (config.listDlgSubtitle) this.listDlgSubtitle = config.listDlgSubtitle;
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.setView("start");
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(EntityExistCheck);
